import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

function formatAmount(amount) {
    if (amount === null || amount === undefined || amount === '') return '-';
    return util.format(amount, 0, ',', ' ');
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string' && dateStr.length >= 10) {
        const parts = dateStr.substring(0, 10).split('-');
        if (parts.length === 3) {
            return `${parts[2]}.${parts[1]}.${parts[0]}`;
        }
    }
    return dateStr;
}

@observer
class TripletexComparison extends Component {
    constructor(props) {
        super(props);
        this.state = {
            filter: 'all',
            sortField: 'saleDate',
            sortDirection: 'desc',
            searchText: '',
            expandedRows: {},
        };
    }

    setFilter = (filter) => {
        this.setState({ filter });
    }

    setSort = (field) => {
        this.setState(prev => ({
            sortField: field,
            sortDirection: prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc',
        }));
    }

    toggleRow = (key) => {
        this.setState(prev => ({
            expandedRows: {
                ...prev.expandedRows,
                [key]: !prev.expandedRows[key],
            },
        }));
    }

    getUnifiedRows() {
        const { stores } = this.props;
        const { tripletexStore } = stores;
        const data = tripletexStore.comparisonData;
        if (!data) return [];

        const rows = [];

        (data.matched || []).forEach(m => {
            rows.push({
                _type: 'matched',
                _key: `m-${m.saleId}-${m.tripletexInvoiceId}`,
                saleNumber: m.saleNumber,
                tripletexInvoiceNumber: m.tripletexInvoiceNumber,
                saleDate: m.saleDate || m.tripletexInvoiceDate,
                customerName: m.customerName || m.tripletexCustomerName,
                localAmount: m.localAmount,
                tripletexAmount: m.tripletexAmount,
                amountDiff: m.amountDiff,
                hasAmountMismatch: m.hasAmountMismatch,
                tripletexAmountOutstanding: m.tripletexAmountOutstanding,
                localPaymentMethod: m.localPaymentMethod,
                kid: m.kid,
                isCreditNote: m.isCreditNote,
                saleId: m.saleId,
                localTotalPaid: m.localTotalPaid,
                localStatus: m.localStatus,
                tripletexInvoiceId: m.tripletexInvoiceId,
                tripletexInvoiceDate: m.tripletexInvoiceDate,
                tripletexAmountRoundoff: m.tripletexAmountRoundoff,
                tripletexCustomerName: m.tripletexCustomerName,
            });
        });

        (data.onlyLocal || []).forEach(m => {
            rows.push({
                _type: 'onlyLocal',
                _key: `l-${m.saleId}`,
                saleNumber: m.saleNumber,
                tripletexInvoiceNumber: null,
                saleDate: m.saleDate,
                customerName: m.customerName,
                localAmount: m.localAmount,
                tripletexAmount: null,
                amountDiff: null,
                hasAmountMismatch: false,
                tripletexAmountOutstanding: null,
                localPaymentMethod: m.paymentMethod,
                kid: null,
                isCreditNote: false,
                saleId: m.saleId,
                localStatus: m.status,
                hasTripletexResponse: m.hasTripletexResponse,
            });
        });

        (data.onlyTripletex || []).forEach(m => {
            rows.push({
                _type: 'onlyTripletex',
                _key: `t-${m.tripletexInvoiceId}`,
                saleNumber: null,
                tripletexInvoiceNumber: m.tripletexInvoiceNumber,
                saleDate: m.tripletexInvoiceDate,
                customerName: m.tripletexCustomerName,
                localAmount: null,
                tripletexAmount: m.tripletexAmount,
                amountDiff: null,
                hasAmountMismatch: false,
                tripletexAmountOutstanding: m.tripletexAmountOutstanding,
                localPaymentMethod: null,
                kid: m.kid,
                isCreditNote: m.isCreditNote,
                tripletexInvoiceId: m.tripletexInvoiceId,
                orderNumbers: m.orderNumbers,
            });
        });

        return rows;
    }

    getFilteredSortedRows() {
        const { filter, sortField, sortDirection, searchText } = this.state;
        let rows = this.getUnifiedRows();

        // Filter
        if (filter === 'matched') {
            rows = rows.filter(r => r._type === 'matched' && !r.hasAmountMismatch);
        } else if (filter === 'mismatches') {
            rows = rows.filter(r => r._type === 'matched' && r.hasAmountMismatch);
        } else if (filter === 'onlyLocal') {
            rows = rows.filter(r => r._type === 'onlyLocal');
        } else if (filter === 'onlyTripletex') {
            rows = rows.filter(r => r._type === 'onlyTripletex');
        } else if (filter === 'creditNotes') {
            rows = rows.filter(r => r.isCreditNote);
        }

        // Search
        if (searchText) {
            const search = searchText.toLowerCase();
            rows = rows.filter(r => {
                const name = (r.customerName || '').toLowerCase();
                const saleNum = String(r.saleNumber || '');
                const invoiceNum = String(r.tripletexInvoiceNumber || '');
                return name.includes(search) || saleNum.includes(search) || invoiceNum.includes(search);
            });
        }

        // Sort
        rows.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            if (aVal === null || aVal === undefined) aVal = '';
            if (bVal === null || bVal === undefined) bVal = '';
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const aStr = String(aVal);
            const bStr = String(bVal);
            return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        });

        return rows;
    }

    getRowClass(row) {
        if (row.isCreditNote) return 'table-secondary';
        if (row._type === 'onlyLocal') return 'table-info';
        if (row._type === 'onlyTripletex') return 'table-danger';
        if (row.hasAmountMismatch) return 'table-warning';
        return '';
    }

    getStatusBadge(row) {
        if (row.isCreditNote) {
            return <span class='badge bg-secondary'>Kreditnota</span>;
        }
        if (row._type === 'onlyLocal') {
            return <span class='badge bg-info'>Kun lokal</span>;
        }
        if (row._type === 'onlyTripletex') {
            return <span class='badge bg-danger'>Kun TT</span>;
        }
        if (row.hasAmountMismatch) {
            return <span class='badge bg-warning text-dark'>Diff</span>;
        }
        return <span class='badge bg-success'>OK</span>;
    }

    renderSortIcon(field) {
        const { sortField, sortDirection } = this.state;
        if (sortField !== field) return null;
        return sortDirection === 'asc'
            ? <i class='fa-solid fa-sort-up ms-1' />
            : <i class='fa-solid fa-sort-down ms-1' />;
    }

    renderExpandedDetail(row) {
        return (
            <tr key={`${row._key}-detail`}>
                <td colspan='11' class='bg-body-secondary p-3'>
                    <div class='d-flex flex-column gap-2' style='font-size: 0.85em;'>
                        {row._type !== 'onlyTripletex' && <>
                            <div>
                                <strong>Lokalt salg:</strong>{' '}
                                <a href={`/dyrejournal-sale/${row.saleId}`}>
                                    Apne salg #{row.saleNumber}
                                </a>
                            </div>
                            <div>
                                <strong>Status:</strong> {row.localStatus || '-'} |{' '}
                                <strong>Betaling:</strong> {row.localPaymentMethod || '-'} |{' '}
                                <strong>Betalt:</strong> {formatAmount(row.localTotalPaid)}
                            </div>
                        </>}
                        {row._type !== 'onlyLocal' && <>
                            <div>
                                <strong>Tripletex faktura:</strong> #{row.tripletexInvoiceNumber} (ID: {row.tripletexInvoiceId})
                            </div>
                            <div>
                                <strong>TT dato:</strong> {formatDate(row.tripletexInvoiceDate)} |{' '}
                                <strong>TT kunde:</strong> {row.tripletexCustomerName || '-'} |{' '}
                                <strong>Avrunding:</strong> {formatAmount(row.tripletexAmountRoundoff)}
                            </div>
                        </>}
                        {row._type === 'onlyTripletex' && row.orderNumbers && row.orderNumbers.length > 0 && (
                            <div>
                                <strong>Ordrenumre:</strong> {row.orderNumbers.join(', ')}
                            </div>
                        )}
                        {row._type === 'onlyLocal' && (
                            <div>
                                <strong>Har Tripletex-respons:</strong> {row.hasTripletexResponse ? 'Ja' : 'Nei'}
                            </div>
                        )}
                    </div>
                </td>
            </tr>
        );
    }

    render() {
        const { stores, onClose, onRefresh } = this.props;
        const { tripletexStore } = stores;
        const { filter, searchText, expandedRows } = this.state;
        const data = tripletexStore.comparisonData;
        const loading = tripletexStore.comparisonLoading;

        if (loading) {
            return (
                <div class='d-flex flex-column align-items-center p-4'>
                    <i class='fa-duotone fa-spinner fa-spin fa-2x mb-3' />
                    <span>Henter data fra Tripletex og sammenligner...</span>
                </div>
            );
        }

        if (!data) {
            return (
                <div class='d-flex flex-column align-items-center p-4'>
                    <span class='text-body-secondary'>Ingen sammenligningsdata. Klikk "Sammenlign" for a starte.</span>
                </div>
            );
        }

        const { summary } = data;
        const rows = this.getFilteredSortedRows();

        return (
            <div class='d-flex flex-column w-100'>
                {/* Header */}
                <div class='d-flex justify-content-between align-items-center mb-3'>
                    <h6 class='mb-0'>
                        <i class='fa-solid fa-code-compare me-2' />
                        Sammenligning: Dyrejournal vs Tripletex
                    </h6>
                    <div class='d-flex gap-2'>
                        <button class='btn btn-sm btn-outline-primary rounded-pill' onClick={onRefresh}>
                            <i class='fa-solid fa-refresh me-1' />Oppdater
                        </button>
                        <button class='btn btn-sm btn-outline-secondary rounded-pill' onClick={onClose}>
                            <i class='fa-solid fa-times me-1' />Lukk
                        </button>
                    </div>
                </div>

                {/* Summary badges */}
                <div class='d-flex flex-wrap gap-2 mb-3'>
                    <button
                        class={`btn btn-sm rounded-pill ${filter === 'all' ? 'btn-dark' : 'btn-outline-dark'}`}
                        onClick={() => this.setFilter('all')}
                    >
                        Alle ({summary.totalMatched + summary.totalOnlyLocal + summary.totalOnlyTripletex})
                    </button>
                    <button
                        class={`btn btn-sm rounded-pill ${filter === 'matched' ? 'btn-success' : 'btn-outline-success'}`}
                        onClick={() => this.setFilter('matched')}
                    >
                        Matchet ({summary.totalMatched - summary.totalAmountMismatches})
                    </button>
                    {summary.totalAmountMismatches > 0 && (
                        <button
                            class={`btn btn-sm rounded-pill ${filter === 'mismatches' ? 'btn-warning' : 'btn-outline-warning'}`}
                            onClick={() => this.setFilter('mismatches')}
                        >
                            Differanser ({summary.totalAmountMismatches})
                        </button>
                    )}
                    <button
                        class={`btn btn-sm rounded-pill ${filter === 'onlyLocal' ? 'btn-info' : 'btn-outline-info'}`}
                        onClick={() => this.setFilter('onlyLocal')}
                    >
                        Kun i Dyrejournal ({summary.totalOnlyLocal})
                    </button>
                    {summary.totalOnlyTripletex > 0 && (
                        <button
                            class={`btn btn-sm rounded-pill ${filter === 'onlyTripletex' ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={() => this.setFilter('onlyTripletex')}
                        >
                            Kun i Tripletex ({summary.totalOnlyTripletex})
                        </button>
                    )}
                </div>

                {/* Totals row */}
                <div class='d-flex flex-wrap gap-3 mb-3 px-2' style='font-size: 0.9em;'>
                    <span>
                        <strong>Lokalt:</strong> kr {formatAmount(summary.localTotalAmount)}
                    </span>
                    <span>
                        <strong>Tripletex:</strong> kr {formatAmount(summary.tripletexTotalAmount)}
                    </span>
                    {summary.totalDiff > 0 && (
                        <span class='text-warning fw-bold'>
                            <strong>Diff:</strong> kr {formatAmount(summary.totalDiff)}
                        </span>
                    )}
                    {summary.hasMore && (
                        <span class='text-danger'>
                            <i class='fa-solid fa-warning me-1' />
                            Viser {summary.fullResultSize > 10000 ? '10000' : summary.fullResultSize} av {summary.fullResultSize} fakturaer
                        </span>
                    )}
                </div>

                {/* Search */}
                <div class='mb-3 px-2'>
                    <input
                        type='text'
                        class='form-control form-control-sm'
                        placeholder='Sok etter kundenavn, salgsnr eller fakturanr...'
                        value={searchText}
                        onInput={(e) => this.setState({ searchText: e.target.value })}
                    />
                </div>

                {/* Date range info */}
                <div class='mb-2 px-2'>
                    <small class='text-body-secondary'>
                        Periode: {formatDate(data.dateFrom)} - {formatDate(data.dateTo)}
                    </small>
                </div>

                {/* Table */}
                <div class='table-responsive'>
                    <table class='table table-sm table-hover mb-0' style='font-size: 0.85em;'>
                        <thead>
                            <tr>
                                <th style='cursor: pointer;' onClick={() => this.setSort('_type')}>
                                    Status {this.renderSortIcon('_type')}
                                </th>
                                <th style='cursor: pointer;' onClick={() => this.setSort('saleNumber')}>
                                    Salg # {this.renderSortIcon('saleNumber')}
                                </th>
                                <th style='cursor: pointer;' onClick={() => this.setSort('tripletexInvoiceNumber')}>
                                    Faktura # {this.renderSortIcon('tripletexInvoiceNumber')}
                                </th>
                                <th style='cursor: pointer;' onClick={() => this.setSort('saleDate')}>
                                    Dato {this.renderSortIcon('saleDate')}
                                </th>
                                <th style='cursor: pointer;' onClick={() => this.setSort('customerName')}>
                                    Kunde {this.renderSortIcon('customerName')}
                                </th>
                                <th class='text-end' style='cursor: pointer;' onClick={() => this.setSort('localAmount')}>
                                    Lokalt {this.renderSortIcon('localAmount')}
                                </th>
                                <th class='text-end' style='cursor: pointer;' onClick={() => this.setSort('tripletexAmount')}>
                                    TT belop {this.renderSortIcon('tripletexAmount')}
                                </th>
                                <th class='text-end' style='cursor: pointer;' onClick={() => this.setSort('amountDiff')}>
                                    Diff {this.renderSortIcon('amountDiff')}
                                </th>
                                <th class='text-end'>Utstaende</th>
                                <th>Betaling</th>
                                <th>KID</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <>
                                    <tr
                                        key={row._key}
                                        class={this.getRowClass(row)}
                                        style='cursor: pointer;'
                                        onClick={() => this.toggleRow(row._key)}
                                    >
                                        <td>{this.getStatusBadge(row)}</td>
                                        <td>{row.saleNumber || '-'}</td>
                                        <td>{row.tripletexInvoiceNumber || '-'}</td>
                                        <td>{formatDate(row.saleDate)}</td>
                                        <td>{row.customerName || '-'}</td>
                                        <td class='text-end'>{formatAmount(row.localAmount)}</td>
                                        <td class='text-end'>{formatAmount(row.tripletexAmount)}</td>
                                        <td class={`text-end ${row.hasAmountMismatch ? 'fw-bold text-warning' : ''}`}>
                                            {row.amountDiff !== null && row.amountDiff !== undefined ? formatAmount(row.amountDiff) : '-'}
                                        </td>
                                        <td class='text-end'>{formatAmount(row.tripletexAmountOutstanding)}</td>
                                        <td>{row.localPaymentMethod || '-'}</td>
                                        <td><code class='text-body-secondary'>{row.kid || '-'}</code></td>
                                    </tr>
                                    {expandedRows[row._key] && this.renderExpandedDetail(row)}
                                </>
                            ))}
                            {rows.length === 0 && (
                                <tr>
                                    <td colspan='11' class='text-center text-body-secondary py-3'>
                                        Ingen rader a vise for valgt filter.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}

export default TripletexComparison;
